import { type DiffLine, lineDiff } from "./diff.ts";

/** A mark on the merged Markdown: text the proposal inserts, or text it removes. */
export interface ChangeMark {
  start: number;
  end: number;
  kind: "ins" | "del";
}

/** The proposed page with its base's removed text kept in place, and where each change sits. */
export interface MarkedMarkdown {
  markdown: string;
  marks: ChangeMark[];
  /** Lines added and removed (the edit's size). */
  added: number;
  removed: number;
}

/** A line that opens or closes a fenced code block: removing one would rewrap what follows. */
const FENCE = /^ {0,3}(`{3,}|~{3,})/;
/** Below this share of words in common, two paired lines read as a replacement, not an edit. */
const SIMILAR = 0.3;

const tokens = (line: string) => line.match(/\s+|[^\s]+/g) ?? [];

class Builder {
  text = "";
  marks: ChangeMark[] = [];
  push(piece: string, kind?: ChangeMark["kind"]) {
    if (!piece) {
      return;
    }
    const start = this.text.length;
    this.text += piece;
    if (!kind) {
      return;
    }
    const last = this.marks.at(-1);
    if (last && last.kind === kind && last.end === start) {
      last.end = this.text.length;
    } else {
      this.marks.push({ start, end: this.text.length, kind });
    }
  }
  line(text: string, kind?: ChangeMark["kind"]) {
    if (this.text) {
      this.text += "\n";
    }
    // The block's syntax (`## `, `- `, `| `) stays unmarked: the renderer marks text leaves.
    const prefix = blockPrefix(text);
    this.push(prefix);
    this.push(text.slice(prefix.length), kind);
  }
}

/** A line's block syntax: heading, list or quote marker, with its indentation. */
const blockPrefix = (text: string) =>
  /^\s*(#{1,6}\s+|[-*+]\s+|\d+[.)]\s+|>\s*)?/.exec(text)?.[0] ?? "";

/** One changed line pair, merged word by word: removed words struck where they stood. */
function mergeWords(out: Builder, before: string, after: string): boolean {
  // A change of block (`##` to `###`, a list item to a paragraph) reads as the block it becomes:
  // the pair is a removal and an insertion, never a merged line.
  const kind = (line: string) => blockPrefix(line).trim().replace(/^\d+/, "1");
  if (kind(before) !== kind(after)) {
    return false;
  }
  const a = tokens(before);
  const b = tokens(after);
  const steps = lineDiff(a.join("\n"), b.join("\n"));
  if (!steps) {
    return false;
  }
  const words = (list: string[]) => list.filter((w) => w.trim()).length;
  const same = words(steps.filter((s) => s.kind === "same").map((s) => s.text));
  if (same / Math.max(words(a), words(b), 1) < SIMILAR) {
    return false;
  }
  if (out.text) {
    out.text += "\n";
  }
  let previous: DiffLine["kind"] | null = null;
  for (const step of steps) {
    // Removed spacing reads as nothing; a removal next to an insertion keeps a space between.
    if (step.kind === "del" && !step.text.trim()) {
      continue;
    }
    if (
      previous &&
      previous !== "same" &&
      step.kind !== "same" &&
      previous !== step.kind &&
      !/\s$/.test(out.text)
    ) {
      out.push(" ");
    }
    out.push(step.text, step.kind === "same" ? undefined : step.kind === "add" ? "ins" : "del");
    previous = step.kind;
  }
  return true;
}

/**
 * The proposed Markdown with the base's removed text kept in place (wiki-collaboration-ux D1):
 * a block-level line diff, then a word diff inside each changed line pair, so the safe Markdown
 * renderer shows the page as it will read with insertions and deletions marked. Removed fence
 * lines are dropped so the proposal's code blocks hold. Null when the diff is
 * too large to compute (the review then opens in Source).
 */
export function markedMarkdown(base: string, proposed: string): MarkedMarkdown | null {
  const diff = lineDiff(base, proposed);
  if (!diff) {
    return null;
  }
  const out = new Builder();
  let added = 0;
  let removed = 0;
  let dels: string[] = [];
  let adds: string[] = [];
  const flush = () => {
    for (let i = 0; i < Math.max(dels.length, adds.length); i++) {
      const del = dels[i];
      const add = adds[i];
      if (del !== undefined && add !== undefined && del.trim() && add.trim()) {
        if (mergeWords(out, del, add)) {
          continue;
        }
      }
      if (del?.trim() && !FENCE.test(del)) {
        out.line(del, "del");
      } else if (del !== undefined && add === undefined && !del.trim()) {
        // A removed paragraph keeps its blank line, so it stays a block of its own.
        out.line("");
      }
      if (add !== undefined) {
        out.line(add, add.trim() ? "ins" : undefined);
      }
    }
    dels = [];
    adds = [];
  };
  for (const line of diff) {
    if (line.kind === "same") {
      flush();
      out.line(line.text);
    } else if (line.kind === "del") {
      removed++;
      dels.push(line.text);
    } else {
      added++;
      adds.push(line.text);
    }
  }
  flush();
  return { markdown: out.text, marks: out.marks, added, removed };
}
